import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';

const MySmokedPage = () => {
  const [smokedCigars, setSmokedCigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchSmokedCigars = async () => {
    try {
      const res = await axios.get(`${API}/smoked/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmokedCigars(res.data);
    } catch (error) {
      toast.error('Failed to fetch smoked cigars');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSmokedCigars();
    }
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] pb-24 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="mb-6 flex justify-center">
            <div className="w-32 h-32 rounded-full bg-white/5 flex items-center justify-center border-2 border-white/10">
              <CheckCircle size={64} className="text-white/30" />
            </div>
          </div>
          <h2 className="playfair text-2xl text-white mb-6">Sign in to view your smoked cigars</h2>
          <Button
            data-testid="smoked-signin-button"
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-br from-amber-600 to-amber-800 text-white px-12 py-6 rounded-full text-lg"
          >
            Sign In
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      <div className="max-w-md mx-auto p-4">
        <h1 className="playfair text-3xl text-white font-bold mb-2">Cigars I've Smoked</h1>
        <p className="text-white/50 text-sm mb-6">{smokedCigars.length} cigar{smokedCigars.length !== 1 ? 's' : ''} in your collection</p>

        <div data-testid="smoked-list" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : smokedCigars.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={48} className="text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No cigars marked as smoked yet</p>
              <p className="text-white/30 text-sm mt-2">Visit a cigar's page and check "Mark as smoked"</p>
            </div>
          ) : (
            smokedCigars.map((cigar) => (
              <div
                key={cigar.id}
                data-testid="smoked-cigar-card"
                onClick={() => navigate(`/cigar/${cigar.id}`)}
                className="bg-[#141416] rounded-2xl p-4 border border-white/5 hover:border-amber-500/30 transition-all cursor-pointer"
              >
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden relative">
                    <CigarImage src={cigar.image_url} alt={cigar.name} className="rounded-lg" />
                    <div className="absolute bottom-1 right-1 bg-amber-600 rounded-full p-0.5">
                      <CheckCircle size={12} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-white/50">{cigar.brand}</p>
                    <h3 className="text-lg font-bold text-white">{cigar.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="text-amber-500 fill-amber-500" size={16} />
                      <span className="text-amber-500">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-white/30">·</span>
                      <span className="text-white/50 text-sm">{cigar.strength || 'Medium'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default MySmokedPage;
